import React, { useEffect, useState, useCallback } from 'react';
import { Rss, Plus, Trash2, RefreshCw, Send, ExternalLink, X, Mail, Building2, Sparkles } from 'lucide-react';
import { sapi } from '../../lib/api';
import { Spinner, useToast, Toast, ConfirmModal } from '../../components/UI';
import { useNavigate } from 'react-router-dom';

interface JobSource { id: string; name: string; feedUrl: string; keywords: string[]; lastRefreshedAt?: string; }
interface DiscoveredJob {
  id: string; sourceName: string; title: string; link: string;
  company: string; extractedEmail: string; description: string; publishedAt: string | null;
}

// Verified real, working feeds — add more here as they're found and checked.
const SUGGESTED_SOURCES = [
  { name: 'iHarare Jobs',  feedUrl: 'https://ihararejobs.com/feed/' },
  { name: 'Jobs Zimbabwe', feedUrl: 'https://jobszimbabwe.co.zw/feed/' },
];

export default function FindJobsPage(): React.ReactElement {
  const { toast, show } = useToast();
  const navigate = useNavigate();

  const [sources, setSources] = useState<JobSource[]>([]);
  const [jobs, setJobs] = useState<DiscoveredJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [deleteSourceTarget, setDeleteSourceTarget] = useState<JobSource | null>(null);

  const [showAddSource, setShowAddSource] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [newKeywords, setNewKeywords] = useState('');
  const [saving, setSaving] = useState(false);
  const [addingSuggested, setAddingSuggested] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, j] = await Promise.all([sapi.getJobSources(), sapi.getDiscoveredJobs()]);
      setSources(s); setJobs(j);
    } catch { setSources([]); setJobs([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const unaddedSuggestions = SUGGESTED_SOURCES.filter(sug => !sources.some(s => s.feedUrl === sug.feedUrl));

  // One click: add the source, then immediately check it, so jobs show up
  // right away instead of needing a second manual Refresh click.
  const addSuggested = async (sug: { name: string; feedUrl: string }) => {
    setAddingSuggested(sug.feedUrl);
    try {
      const created = await sapi.createJobSource({ name: sug.name, feedUrl: sug.feedUrl, keywords: [] });
      const { added } = await sapi.refreshJobSource(created.id);
      show(added > 0 ? `Added ${sug.name} — found ${added} listing${added === 1 ? '' : 's'}` : `Added ${sug.name}`, 'success');
      await load();
    } catch (e: any) { show(e.message || `Failed to add ${sug.name}`, 'error'); }
    finally { setAddingSuggested(null); }
  };

  const addSource = async () => {
    if (!newName.trim() || !newFeedUrl.trim()) { show('Name and feed URL are required', 'error'); return; }
    setSaving(true);
    try {
      const keywords = newKeywords.split(',').map(k => k.trim()).filter(Boolean);
      await sapi.createJobSource({ name: newName.trim(), feedUrl: newFeedUrl.trim(), keywords });
      show('Job source added', 'success');
      setShowAddSource(false); setNewName(''); setNewFeedUrl(''); setNewKeywords('');
      await load();
    } catch (e: any) { show(e.message || 'Failed to add source', 'error'); }
    finally { setSaving(false); }
  };

  const refresh = async (source: JobSource) => {
    setRefreshingId(source.id);
    try {
      const { added } = await sapi.refreshJobSource(source.id);
      show(added > 0 ? `Found ${added} new listing${added === 1 ? '' : 's'}` : 'No new listings since last check', added > 0 ? 'success' : 'info');
      await load();
    } catch (e: any) { show(e.message || 'Refresh failed', 'error'); }
    finally { setRefreshingId(null); }
  };

  const deleteSource = async () => {
    if (!deleteSourceTarget) return;
    await sapi.deleteJobSource(deleteSourceTarget.id).catch(() => null);
    setSources(p => p.filter(s => s.id !== deleteSourceTarget.id));
    show('Job source removed', 'info'); setDeleteSourceTarget(null);
  };

  const dismiss = async (job: DiscoveredJob) => {
    setJobs(p => p.filter(j => j.id !== job.id));
    await sapi.dismissDiscoveredJob(job.id).catch(() => null);
  };

  // Same { company } shape CompaniesPage already sends to Apply — the Apply
  // page itself is untouched, this just pre-fills what it already expects.
  const draftApplication = (job: DiscoveredJob) => {
    navigate('/student/apply', {
      state: { company: { name: job.company, email: job.extractedEmail, address: '', managerName: '' } },
    });
  };

  if (loading) return <div className="page" style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}><Spinner size={28} /></div>;

  return (
    <div className="page fade-in">
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      <div className="page-header">
        <div>
          <h1 className="page-title">Find Jobs</h1>
          <p className="page-sub">Watches job feeds you choose and drafts the Apply form for you — you still review and hit Send</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddSource(v => !v)}><Plus size={14} /> Add Source</button>
      </div>

      {/* Add source form */}
      {showAddSource && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>New Job Source</div>
          <div className="form-row">
            <div className="form-group">
              <label>Name</label>
              <input placeholder="e.g. Jobs Zimbabwe" value={newName} onChange={e => setNewName(e.target.value)} />
            </div>
            <div className="form-group">
              <label>RSS Feed URL</label>
              <input placeholder="https://example.com/feed/" value={newFeedUrl} onChange={e => setNewFeedUrl(e.target.value)} />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Keywords (comma-separated — leave blank to match everything)</label>
            <input placeholder="e.g. developer, IT, software" value={newKeywords} onChange={e => setNewKeywords(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="btn btn-primary" onClick={addSource} disabled={saving}>{saving ? <Spinner size={14} /> : <Plus size={14} />} Add Source</button>
            <button className="btn btn-ghost" onClick={() => setShowAddSource(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Suggested sources — verified working feeds, one click to add + check */}
      {unaddedSuggestions.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
            <Sparkles size={14} color="var(--accent)" /> Suggested Zimbabwe job feeds
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {unaddedSuggestions.map(sug => (
              <button key={sug.feedUrl} className="btn btn-ghost btn-sm" onClick={() => addSuggested(sug)} disabled={addingSuggested === sug.feedUrl}>
                {addingSuggested === sug.feedUrl ? <Spinner size={12} /> : <Plus size={12} />} {sug.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sources */}
      {sources.length === 0 && !showAddSource ? (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="empty-state">
            <Rss size={32} />
            <h3>No job sources yet</h3>
            <p>Add one of the suggestions above, or add any job board's own RSS feed.</p>
          </div>
        </div>
      ) : sources.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
          {sources.map(s => (
            <div key={s.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px' }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(108,99,255,0.1)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Rss size={15} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.keywords?.length ? `Keywords: ${s.keywords.join(', ')}` : 'Matching all listings'}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => refresh(s)} disabled={refreshingId === s.id}>
                {refreshingId === s.id ? <Spinner size={12} /> : <RefreshCw size={12} />} Refresh
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => setDeleteSourceTarget(s)}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Discovered jobs */}
      {sources.length > 0 && (
        <>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Discovered Listings ({jobs.length})</div>
          {jobs.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <Send size={28} />
                <h3>Nothing yet</h3>
                <p>Hit Refresh on a source above to check it for new listings.</p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {jobs.map(job => (
                <div key={job.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{job.title}</div>
                      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11, color: 'var(--text3)' }}>
                        <span>{job.sourceName}</span>
                        {job.company && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Building2 size={11} /> {job.company}</span>}
                        {job.extractedEmail && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--accent3)' }}><Mail size={11} /> {job.extractedEmail}</span>}
                      </div>
                    </div>
                    <button onClick={() => dismiss(job)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', flexShrink: 0 }}><X size={16} /></button>
                  </div>

                  {job.description && (
                    <p style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6, marginTop: 10, marginBottom: 0 }}>
                      {job.description.slice(0, 220)}{job.description.length > 220 ? '…' : ''}
                    </p>
                  )}

                  <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => draftApplication(job)}>
                      <Send size={12} /> Draft Application
                    </button>
                    <a href={job.link} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                      <ExternalLink size={12} /> View Listing
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {deleteSourceTarget && (
        <ConfirmModal
          title="Remove Job Source"
          message={<>Stop watching <strong>{deleteSourceTarget.name}</strong>? Listings already found from it will stay in your list.</>}
          onConfirm={deleteSource} onCancel={() => setDeleteSourceTarget(null)} danger
        />
      )}
    </div>
  );
}
